// Auth gate for GCIP-backed tenants — the replacement for aws-amplify-react's
// <Authenticator> on tenants that do not use Cognito.
//
// The Amplify Authenticator is mounted with hide={[SignIn, Loading, Greetings]},
// i.e. all of its UI is already suppressed and it acts purely as a session
// wrapper that redirects to a hosted IdP. That is why this replacement is small:
// it only has to answer "is there a session?" and render a sign-in form if not.

import { useCallback, useEffect, useState } from "react";

import BrandMark from "../components/BrandMark";
import { getBrand, getProductConfig } from "../config/product";
import {
  getCurrentUser,
  getIdToken,
  hasRequiredClaims,
  isSignedIn,
  signInWithGoogleCredential,
  signInWithPassword,
  signOut,
} from "./gcip";

/** Turn GCIP's machine error codes into something a human can act on. */
function humanise(code: string): string {
  if (/INVALID_LOGIN_CREDENTIALS|INVALID_PASSWORD|EMAIL_NOT_FOUND/.test(code))
    return "That email and password don't match an account.";
  if (/USER_DISABLED/.test(code)) return "This account has been disabled.";
  if (/TOO_MANY_ATTEMPTS/.test(code))
    return "Too many attempts — wait a moment and try again.";
  if (/SESSION_EXPIRED/.test(code))
    return "Your session expired. Please sign in again.";
  if (/NETWORK|Failed to fetch/i.test(code))
    return "Couldn't reach the sign-in service. Check your connection.";
  return code;
}

const S: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#1F2124",
    fontFamily: 'Roboto,-apple-system,BlinkMacSystemFont,"Segoe UI",Arial,sans-serif',
    padding: 24,
  },
  card: {
    width: "100%",
    maxWidth: 396,
    background: "#fff",
    borderRadius: 12,
    padding: 34,
    boxShadow: "0 18px 48px rgba(0,0,0,.34)",
  },
  brand: { display: "flex", alignItems: "center", gap: 11, marginBottom: 26 },
  name: {
    fontFamily: '"Roboto Condensed",Roboto,"Arial Narrow",sans-serif',
    fontWeight: 900,
    fontSize: 27,
    letterSpacing: ".045em",
    textTransform: "uppercase",
    color: "#1F2124",
  },
  label: {
    display: "block",
    fontSize: 12.5,
    fontWeight: 700,
    letterSpacing: ".08em",
    textTransform: "uppercase",
    color: "#6B6560",
    marginBottom: 6,
  },
  input: {
    width: "100%",
    padding: "11px 13px",
    fontSize: 15,
    border: "1px solid #D9D2CB",
    borderRadius: 7,
    marginBottom: 16,
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  btn: {
    width: "100%",
    padding: "12px 16px",
    fontSize: 15,
    fontWeight: 700,
    color: "#fff",
    background: "#008e92",
    border: 0,
    borderRadius: 7,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  err: {
    background: "#FDECE7",
    border: "1px solid #F3C0AE",
    color: "#8C3312",
    padding: "10px 13px",
    borderRadius: 7,
    fontSize: 14,
    marginBottom: 16,
  },
  note: { fontSize: 13, color: "#6B6560", marginTop: 18, lineHeight: 1.5 },
  divider: {
    display: "flex",
    alignItems: "center",
    margin: "18px 0",
    color: "#8C857E",
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: ".06em",
  },
  dividerLine: {
    flex: 1,
    height: 1,
    background: "#E5DFD9",
  },
};

const GcipGate: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [ready, setReady] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [claimsOk, setClaimsOk] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const brand = getBrand();
  const tenantTitle = brand.wordmark || getProductConfig().installName.toUpperCase();
  const btnStyle = { ...S.btn, background: brand.accent };

  const sync = useCallback(async () => {
    const inSession = isSignedIn();
    if (!inSession) {
      setSignedIn(false);
      setClaimsOk(true);
      setReady(true);
      return;
    }
    const token = await getIdToken();
    if (!token) {
      signOut();
      setSignedIn(false);
      setClaimsOk(true);
      setReady(true);
      return;
    }
    setSignedIn(true);
    setClaimsOk(hasRequiredClaims());
    setReady(true);
  }, []);

  useEffect(() => {
    sync();
  }, [sync]);

  useEffect(() => {
    if (signedIn) return;

    const handleGoogleResponse = async (response: any) => {
      if (response?.credential) {
        setBusy(true);
        setError("");
        try {
          await signInWithGoogleCredential(response.credential);
          sync();
        } catch (err) {
          setError(humanise(err instanceof Error ? err.message : String(err)));
        } finally {
          setBusy(false);
        }
      }
    };

    const renderGoogleBtn = () => {
      if ((window as any).google?.accounts?.id) {
        try {
          (window as any).google.accounts.id.initialize({
            client_id: "110933780770-93869boqkqslhcbl7b5g6m46k91vr28t.apps.googleusercontent.com",
            callback: handleGoogleResponse,
          });
          const btnElem = document.getElementById("google-signin-container");
          if (btnElem) {
            btnElem.innerHTML = "";
            (window as any).google.accounts.id.renderButton(btnElem, {
              theme: "outline",
              size: "large",
              width: 328,
              text: "signin_with",
            });
          }
        } catch (err) {
          console.warn("Failed to render Google Sign-In button:", err);
        }
      }
    };

    if (!(window as any).google?.accounts?.id) {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = renderGoogleBtn;
      document.body.appendChild(script);
    } else {
      renderGoogleBtn();
    }
  }, [signedIn, sync]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await signInWithPassword(email.trim(), password);
      sync();
    } catch (err) {
      setError(humanise(err instanceof Error ? err.message : String(err)));
    } finally {
      setBusy(false);
    }
  };

  if (!ready) return null;

  // Authenticated but unprovisioned: the token is valid, yet the backend cannot
  // resolve a user from it. Say so plainly instead of letting every API call
  // fail behind a loading spinner.
  if (signedIn && !claimsOk) {
    const u = getCurrentUser();
    return (
      <div style={S.page}>
        <div style={S.card}>
          <div style={S.brand}>
            <BrandMark size={34} color="#1F2124" />
          </div>
          <div style={S.err}>
            Signed in as {u?.email}, but this account isn't linked to a {tenantTitle}
            user record yet, so the app can't load your data.
          </div>
          <p style={S.note}>
            An administrator needs to provision it (
            <code>gcip_provision_user.py</code>). Until then nothing here will load.
          </p>
          <button
            style={{ ...btnStyle, marginTop: 14 }}
            onClick={() => {
              signOut();
              sync();
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  const isLocal = typeof window !== "undefined" && (window.location.hostname.endsWith(".localhost") || window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
  if (isLocal) return <>{children}</>;

  if (signedIn) return <>{children}</>;

  return (
    <div style={S.page}>
      <form style={S.card} onSubmit={submit}>
        <div style={S.brand}>
          {(() => {
            const logo = brand.logo;
            const logoSrc = logo.kind === "asset" 
              ? (logo.file.startsWith("/") ? (process.env.PUBLIC_URL || "") + logo.file : (process.env.PUBLIC_URL || "") + "/" + logo.file)
              : "";
            return logo.kind === "asset" ? (
              // No plate behind the mark. The dark #111827 panel that used to sit
              // here existed for g2_logo.png (a light-on-dark mark); every asset
              // logo now in play is a transparent wordmark that reads on the white
              // card, so the plate rendered as a black box around the logo.
              <img src={logoSrc} alt={tenantTitle} style={{ height: 32, width: "auto", display: "block" }} />
            ) : (
              <BrandMark size={34} color="#1F2124" />
            );
          })()}
        </div>
        {error && <div style={S.err}>{error}</div>}
        <label style={S.label} htmlFor="gcip-email">
          Email
        </label>
        <input
          id="gcip-email"
          style={S.input}
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <label style={S.label} htmlFor="gcip-password">
          Password
        </label>
        <input
          id="gcip-password"
          style={S.input}
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button style={btnStyle} type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>

        <div style={S.divider}>
          <div style={S.dividerLine} />
          <span style={{ padding: "0 10px" }}>OR</span>
          <div style={S.dividerLine} />
        </div>

        <div id="google-signin-container" style={{ display: "flex", justifyContent: "center", minHeight: 40 }} />

        <p style={S.note}>
          {tenantTitle} uses Google Cloud Identity Platform. Forgotten your password? Ask your
          administrator to send a reset link.
        </p>
      </form>
    </div>
  );
};

export default GcipGate;
